import React, { useRef, useMemo, useContext, useEffect } from 'react';
import CorrectionField from './CorrectionField';
import useObserveQuery from '../../hooks/useObserveQuery';
import { DataTypeOption } from '../../fields/FieldsSettings/FieldsSettingsTypes';
import { IExtraction, IToken } from '../../../types/my-types';
import { featureFlags } from '../../../utils/featureFlags';
import { IValidationConfig } from '../../../types/my-types';
import { MarkStateContext } from '../../../context/MarkContext';
import MarkReviewFormStyles from '../../mark/form/MarkReviewForm.module.scss';
import {
  IValidationErrorByExtraction,
  IValidationErrorByExtractionDetails,
} from '../../../types/validation-types';

export interface IFieldReview {
  content?: string;
  data_type?: string;
  data_type_config?: DataTypeOption;
  glynt_tags?: string[];
  id: string;
  label: string;
  tags?: string[];
  tokens: IToken[];
  validation_config: IValidationConfig[] | null | [];
  field_order?: number;
}

export interface FieldListProps {
  fields: IFieldReview[];
  extraction: IExtraction;
  ebId?: string;
  readOnly?: boolean;
}

interface IPriorityOrder {
  fieldListToUse: IFieldReview[];
  allHighlightedFieldNames: string[];
}

const NO_HIGHLIGHTED_FIELDS: string[] = [];
const NO_VALIDATION_ERRORS: IValidationErrorByExtractionDetails[] = [];

/**
 * Move the fields the extraction batch prioritises (by `fieldPriority:` tags) to the top of
 * the list, keeping their original order, and name them so their rows are highlighted.
 */
const orderFieldsByPriority = (
  fields: IFieldReview[],
  batchTags: string[] | undefined
): IPriorityOrder => {
  if (!batchTags || batchTags.length === 0 || !featureFlags.modifyFieldOrderFromEBTags) {
    return { fieldListToUse: fields, allHighlightedFieldNames: NO_HIGHLIGHTED_FIELDS };
  }

  const fieldNamesToHighlight: string[] = [];
  const tagNamesToHighlight: string[] = [];
  const fieldNamesThatMatchTags: string[] = [];

  batchTags.forEach((tag: string) => {
    if (tag.startsWith('fieldPriority:tag:')) {
      tagNamesToHighlight.push(tag.replace('fieldPriority:tag:', ''));
    } else if (tag.startsWith('fieldPriority:name:')) {
      fieldNamesToHighlight.push(tag.replace('fieldPriority:name:', ''));
    }
  });

  fields.forEach((field: IFieldReview) => {
    const hasCommonTag = field?.glynt_tags?.some((tag: string) =>
      tagNamesToHighlight.includes(tag)
    );
    if (hasCommonTag && !fieldNamesToHighlight.includes(field.label)) {
      fieldNamesThatMatchTags.push(field.label);
    }
  });

  const allHighlightedFieldNames = [...fieldNamesToHighlight, ...fieldNamesThatMatchTags];

  // Highlighted fields go first, in the order they were originally in
  const fieldListToUse = [
    ...fields.filter((field) => allHighlightedFieldNames.includes(field.label)),
    ...fields.filter((field) => !allHighlightedFieldNames.includes(field.label)),
  ];

  return { fieldListToUse, allHighlightedFieldNames };
};

const FieldList: React.FC<FieldListProps> = ({
  extraction,
  fields,
  ebId = '',
  readOnly = false,
}) => {
  const { activeField, fieldListFocused, validationSummaryByExtractionId } =
    useContext(MarkStateContext);

  const fieldRefs: any = useRef({});

  const { data: extractionBatchData }: any = useObserveQuery(['extractionBatch', ebId]);

  //  const corrections: ICorrection[] = useCorrections(extraction.id);
  // console.log({ extraction });
  // TODO: can we avoid the correction query?
  // get results from extraction, extraction list does not contain results
  /*
	const extractionQuery = useQuery(GET_EXTRACTION, {
		variables: { dataPoolId, id: extraction.id },
		fetchPolicy: "network-only",
	});
	*/

  const priorityTags: string[] | undefined = extractionBatchData?.tags;

  const { fieldListToUse, allHighlightedFieldNames } = useMemo(
    () => orderFieldsByPriority(fields, priorityTags),
    [fields, priorityTags]
  );

  const fieldLabelsKey = JSON.stringify(fieldListToUse.map((field) => field.label));
  const fieldLabels: string[] = useMemo(() => JSON.parse(fieldLabelsKey), [fieldLabelsKey]);

  // Get all validation errors for this extraction id
  const allValidationErrors = validationSummaryByExtractionId?.find(
    (v: IValidationErrorByExtraction) => v.extractionId === extraction.id
  );

  const validationErrorsByField = useMemo(() => {
    const byField = new Map<string, IValidationErrorByExtractionDetails[]>();
    allValidationErrors?.validationErrorDetails?.forEach((error) => {
      byField.set(error.field, [...(byField.get(error.field) ?? []), error]);
    });
    return byField;
  }, [allValidationErrors]);

  useMemo(() => {
    fieldListToUse.forEach((field) => {
      const fieldLabel = field.label;
      // Only create the ref if it doesn't already exist
      if (!fieldRefs.current[fieldLabel]) {
        fieldRefs.current[fieldLabel] = React.createRef();
      }
    });
  }, [fieldListToUse]);

  // Only scroll fields into view when they are selected by the spreadsheet
  useEffect(() => {
    // do not scroll if we are navigating through the field list
    if (fieldListFocused) {
      return;
    }

    const element = fieldRefs.current[activeField]?.current;

    if (element) {
      element.scrollIntoView();
    }
  }, [fieldRefs, activeField, fieldListFocused]);

  const isAdditionalField = (fieldLabel: string): boolean => {
    return !extraction?.additional_fields.includes(fieldLabel);
  };

  return (
    <form id="correction-form" className={MarkReviewFormStyles.fieldList}>
      {fieldListToUse.map((field, index) => {
        const fieldIsAdditional = isAdditionalField(field.label);

        return (
          <div ref={fieldRefs.current[field.label]} key={`div-${field.label}-${index}`}>
            <CorrectionField
              key={`${field.label}-${index}`}
              index={index}
              field={field}
              fieldsCount={fields.length}
              fieldId={field.id}
              ebId={ebId}
              extractionId={extraction.id}
              validationErrorsForThisField={
                validationErrorsByField.get(field.label) ?? NO_VALIDATION_ERRORS
              }
              highlightField={allHighlightedFieldNames.includes(field.label)}
              isAdditionalField={fieldIsAdditional}
              fields={fieldLabels}
              readOnly={readOnly}
            />
          </div>
        );
      })}
    </form>
  );
};

export default FieldList;
